import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import { Alert, Button, Field, Input, Select, Stack, TextArea, cx } from '@integra/ui';
import { useCreateCampaign } from '../api/hooks.js';
import type { DagNode } from '../api/types.js';

/**
 * Visual campaign builder (§20.4 — "Campaign builder (visual DAG editor)").
 *
 * A campaign is a DAG of Action nodes (§8.1). This is a drag-and-connect canvas:
 * drop actions from the palette, wire dependencies by dragging between handles,
 * and edit the selected node in the side panel. An edge A→B means "B depends on
 * A" — B waits until A has settled (§8.1), which is exactly the arrow direction
 * an operator reads top-to-bottom.
 *
 * The canvas is the authoring surface; the server re-validates every DAG on save
 * (§13.5) and remains the authority. The read-only CampaignDAGViewer on the
 * execution monitor shows the same graph while it runs.
 */

type ActionKind = 'ingest' | 'transform' | 'render' | 'approve' | 'publish' | 'verify' | 'notify';

interface ActionMeta {
  readonly label: string;
  readonly glyph: string;
  /** Tailwind classes (explicit, so Tailwind's scanner keeps them). */
  readonly accentBar: string;
  readonly badge: string;
  readonly hint: string;
}

/** The action vocabulary the engine understands (§8.1), with its visual identity. */
const ACTION_META: Record<ActionKind, ActionMeta> = {
  ingest: {
    label: 'Ingest',
    glyph: '↓',
    accentBar: 'bg-state-info',
    badge: 'bg-state-info/10 text-state-info',
    hint: 'Pull records from a Source into Assets.',
  },
  transform: {
    label: 'Transform',
    glyph: '⇄',
    accentBar: 'bg-navy-500',
    badge: 'bg-navy-500/10 text-navy-600',
    hint: 'Map or normalise Assets before rendering.',
  },
  render: {
    label: 'Render',
    glyph: '◆',
    accentBar: 'bg-gold-500',
    badge: 'bg-gold-500/15 text-gold-text',
    hint: 'Generate channel-native creative from a Template.',
  },
  approve: {
    label: 'Approve',
    glyph: '✓',
    accentBar: 'bg-state-warning',
    badge: 'bg-state-warning/15 text-state-warning',
    hint: 'Pause for a human approval gate.',
  },
  publish: {
    label: 'Publish',
    glyph: '▲',
    accentBar: 'bg-state-success',
    badge: 'bg-state-success/12 text-state-success',
    hint: 'Push an Asset to a Destination.',
  },
  verify: {
    label: 'Verify',
    glyph: '⊙',
    accentBar: 'bg-state-info',
    badge: 'bg-state-info/10 text-state-info',
    hint: 'Confirm the publishes actually landed.',
  },
  notify: {
    label: 'Notify',
    glyph: '◈',
    accentBar: 'bg-navy-400',
    badge: 'bg-navy-400/15 text-navy-600',
    hint: 'Send a notification on completion.',
  },
};

const ACTION_ORDER: readonly ActionKind[] = [
  'ingest',
  'transform',
  'render',
  'approve',
  'publish',
  'verify',
  'notify',
];

const FAILURE_MODES = ['halt', 'skip', 'continue'] as const;

/** Data carried by each canvas node. `[key: string]` satisfies React Flow's index-signature. */
interface ActionNodeData {
  nodeId: string;
  action: ActionKind;
  inputsText: string;
  failureMode: string;
  [key: string]: unknown;
}

type ActionRfNode = Node<ActionNodeData, 'action'>;

/** A single Action node on the canvas — themed with the design tokens (§17.1). */
function ActionNode({ data, selected }: NodeProps<ActionRfNode>) {
  const meta = ACTION_META[data.action];
  return (
    <div
      className={cx(
        'w-52 overflow-hidden rounded-md border bg-surface shadow-card transition-shadow duration-fast',
        selected ? 'border-gold-500 shadow-raised' : 'border-edge',
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2 !border-surface !bg-navy-400"
      />
      <div className={cx('h-1.5 w-full', meta.accentBar)} />
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <span
          className={cx(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-base font-semibold',
            meta.badge,
          )}
          aria-hidden
        >
          {meta.glyph}
        </span>
        <div className="min-w-0">
          <p className="truncate font-heading text-sm text-navy-700" title={data.nodeId}>
            {data.nodeId || 'untitled'}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-ink-muted">{meta.label}</p>
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-2.5 !w-2.5 !border-2 !border-surface !bg-navy-400"
      />
    </div>
  );
}

const nodeTypes = { action: ActionNode };

const edgeOptions = {
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
  style: { strokeWidth: 1.5 },
} as const;

/** Build the §8.5 worked example, laid out in dependency layers. */
function exampleGraph(): { nodes: ActionRfNode[]; edges: Edge[] } {
  // [rfId, action, nodeId, layer, columnWithinLayer, inputs]
  const spec: [string, ActionKind, string, number, number, string][] = [
    ['n1', 'ingest', 'ingest-catalogue', 0, 0, '{\n  "sourceId": "REPLACE_WITH_SOURCE_ID"\n}'],
    [
      'n2',
      'render',
      'render-square',
      1,
      0,
      '{\n  "templateId": "REPLACE_ME",\n  "format": "jpeg"\n}',
    ],
    [
      'n3',
      'render',
      'render-story',
      1,
      1,
      '{\n  "templateId": "REPLACE_ME",\n  "format": "jpeg"\n}',
    ],
    ['n4', 'approve', 'approve-creative', 2, 0, '{\n  "approverRole": "workspace_admin"\n}'],
    ['n5', 'publish', 'publish-instagram', 3, 0, '{\n  "destinationId": "REPLACE_ME"\n}'],
    ['n6', 'publish', 'publish-facebook', 3, 1, '{\n  "destinationId": "REPLACE_ME"\n}'],
    ['n7', 'publish', 'publish-linkedin', 3, 2, '{\n  "destinationId": "REPLACE_ME"\n}'],
    ['n8', 'verify', 'verify-live', 4, 0, '{}'],
    ['n9', 'notify', 'notify-team', 5, 0, '{\n  "channel": "launches"\n}'],
  ];
  const deps: [string, string][] = [
    ['n1', 'n2'],
    ['n1', 'n3'],
    ['n2', 'n4'],
    ['n3', 'n4'],
    ['n4', 'n5'],
    ['n4', 'n6'],
    ['n4', 'n7'],
    ['n5', 'n8'],
    ['n6', 'n8'],
    ['n7', 'n8'],
    ['n8', 'n9'],
  ];
  const nodes: ActionRfNode[] = spec.map(([id, action, nodeId, layer, col, inputsText]) => ({
    id,
    type: 'action',
    position: { x: 120 + col * 240, y: 40 + layer * 150 },
    data: { nodeId, action, inputsText, failureMode: 'halt' },
  }));
  const edges: Edge[] = deps.map(([source, target]) => ({
    id: `${source}-${target}`,
    source,
    target,
    ...edgeOptions,
  }));
  return { nodes, edges };
}

/** Parse a node's inputs JSON, reporting the problem rather than throwing. */
function parseInputs(text: string): { ok: true; value: Record<string, unknown> } | { ok: false } {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: {} };
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
      return { ok: false };
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch {
    return { ok: false };
  }
}

/**
 * Turn the canvas into a DAG, mirroring the server's validation (§8.1) so the
 * operator gets a specific message instead of a round-trip and a generic 400.
 * The server re-validates and stays the authority.
 */
function compile(
  nodes: readonly ActionRfNode[],
  edges: readonly Edge[],
): { ok: true; dag: DagNode[] } | { ok: false; problems: string[] } {
  const problems: string[] = [];
  if (nodes.length === 0) problems.push('A campaign needs at least one node.');

  const idOf = new Map(nodes.map((n) => [n.id, n.data.nodeId.trim()]));
  const seen = new Set<string>();
  for (const node of nodes) {
    const id = node.data.nodeId.trim();
    if (id === '') {
      problems.push('Every node needs a non-empty id.');
    } else if (seen.has(id)) {
      problems.push(`Duplicate node id "${id}".`);
    } else {
      seen.add(id);
    }
    if (!parseInputs(node.data.inputsText).ok) {
      problems.push(`Inputs for "${id || node.id}" are not a JSON object.`);
    }
  }

  // dependsOn = the business ids of every node with an edge into this one.
  const dependsOn = new Map<string, string[]>();
  for (const node of nodes) dependsOn.set(node.id, []);
  for (const edge of edges) {
    if (edge.source === edge.target) {
      problems.push(`A node cannot depend on itself.`);
      continue;
    }
    const sourceBusinessId = idOf.get(edge.source);
    const list = dependsOn.get(edge.target);
    if (sourceBusinessId !== undefined && list !== undefined) list.push(sourceBusinessId);
  }

  // Cycle check over the canvas edges (business ids not needed — use rf ids).
  if (hasCycle(nodes, edges)) {
    problems.push('The graph has a dependency cycle: a node can never become ready.');
  }

  if (problems.length > 0) return { ok: false, problems: [...new Set(problems)] };

  const dag: DagNode[] = nodes.map((node) => {
    const parsed = parseInputs(node.data.inputsText);
    return {
      nodeId: node.data.nodeId.trim(),
      action: node.data.action,
      inputs: parsed.ok ? parsed.value : {},
      dependsOn: dependsOn.get(node.id) ?? [],
    };
  });
  return { ok: true, dag };
}

/** DFS cycle detection over React Flow ids. */
function hasCycle(nodes: readonly ActionRfNode[], edges: readonly Edge[]): boolean {
  const out = new Map<string, string[]>();
  for (const n of nodes) out.set(n.id, []);
  for (const e of edges) out.get(e.source)?.push(e.target);
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const colour = new Map<string, number>(nodes.map((n) => [n.id, WHITE]));
  const visit = (id: string): boolean => {
    colour.set(id, GREY);
    for (const next of out.get(id) ?? []) {
      const c = colour.get(next);
      if (c === GREY) return true;
      if (c === WHITE && visit(next)) return true;
    }
    colour.set(id, BLACK);
    return false;
  };
  for (const n of nodes) if (colour.get(n.id) === WHITE && visit(n.id)) return true;
  return false;
}

function BuilderInner() {
  const navigate = useNavigate();
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const createCampaign = useCreateCampaign(workspaceId);

  const [nodes, setNodes, onNodesChange] = useNodesState<ActionRfNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [name, setName] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const counter = useRef(0);

  const selected = useMemo(
    () => nodes.find((n) => n.id === selectedId) ?? null,
    [nodes, selectedId],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source === connection.target) return;
      setEdges((eds) => addEdge({ ...connection, ...edgeOptions }, eds));
    },
    [setEdges],
  );

  const addNode = useCallback(
    (action: ActionKind): void => {
      counter.current += 1;
      const n = counter.current;
      const rfId = `node_${String(n)}`;
      const newNode: ActionRfNode = {
        id: rfId,
        type: 'action',
        position: { x: 160 + (n % 4) * 60, y: 80 + n * 40 },
        data: { nodeId: `${action}-${String(n)}`, action, inputsText: '{}', failureMode: 'halt' },
      };
      setNodes((nds) => [...nds, newNode]);
      setSelectedId(rfId);
    },
    [setNodes],
  );

  const patchSelected = useCallback(
    (patch: Partial<ActionNodeData>): void => {
      if (selectedId === null) return;
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedId ? { ...node, data: { ...node.data, ...patch } } : node,
        ),
      );
    },
    [selectedId, setNodes],
  );

  const loadExample = useCallback((): void => {
    const graph = exampleGraph();
    counter.current = graph.nodes.length;
    setNodes(graph.nodes);
    setEdges(graph.edges);
    setProblems([]);
    setSelectedId(null);
    if (name.trim() === '') setName('Four-channel launch');
  }, [name, setNodes, setEdges]);

  const save = useCallback((): void => {
    if (name.trim() === '') {
      setProblems(['Give the campaign a name.']);
      return;
    }
    const result = compile(nodes, edges);
    if (!result.ok) {
      setProblems(result.problems);
      return;
    }
    setProblems([]);
    createCampaign.mutate(
      { name: name.trim(), dag: { nodes: result.dag } },
      { onSuccess: (campaign) => navigate(`/w/${workspaceId}/campaigns/${campaign.id}`) },
    );
  }, [name, nodes, edges, createCampaign, navigate, workspaceId]);

  const inputsValid = selected === null || parseInputs(selected.data.inputsText).ok;

  return (
    <div className="flex h-[calc(100vh-3.25rem)] flex-col bg-surface-muted">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-edge bg-surface px-4 py-2.5 sm:px-5">
        <button
          type="button"
          onClick={() => navigate(`/w/${workspaceId}/campaigns`)}
          className="text-xs uppercase tracking-wide text-ink-muted hover:text-navy-600"
        >
          ← Campaigns
        </button>
        <div className="hidden h-5 w-px bg-edge sm:block" />
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Campaign name"
          className="min-w-[10rem] flex-1 sm:max-w-xs sm:flex-none"
        />
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={loadExample}>
            Load example
          </Button>
          <Button size="sm" onClick={save} loading={createCampaign.isPending}>
            Save campaign
          </Button>
        </div>
      </div>

      {(problems.length > 0 || createCampaign.error !== null) && (
        <div className="border-b border-edge bg-surface px-5 py-2">
          {problems.length > 0 && (
            <Alert tone="error" title="Fix these before saving">
              <ul className="list-disc pl-4">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Alert>
          )}
          {createCampaign.error !== null && (
            <Alert tone="error" title="The server rejected this campaign">
              {createCampaign.error instanceof Error
                ? createCampaign.error.message
                : 'Unknown error'}
            </Alert>
          )}
        </div>
      )}

      {/* Below `lg` the three panes stack and the whole body scrolls; from `lg`
          up it is the classic palette | canvas | inspector row. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-visible">
        {/* Palette */}
        <aside
          aria-label="Action palette"
          className="w-full shrink-0 border-b border-edge bg-surface px-3 py-4 lg:w-44 lg:border-b-0 lg:border-r"
        >
          <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
            Actions
          </p>
          {/* A wrapping row of chips on mobile; the familiar vertical list from lg up. */}
          <div className="flex flex-wrap gap-2 lg:flex-col lg:gap-1">
            {ACTION_ORDER.map((action) => {
              const meta = ACTION_META[action];
              return (
                <button
                  key={action}
                  type="button"
                  onClick={() => addNode(action)}
                  title={meta.hint}
                  className="flex items-center gap-2 rounded-md border border-edge bg-surface px-2.5 py-2 text-left transition-colors duration-fast hover:border-gold-400 hover:bg-surface-sunken"
                >
                  <span
                    className={cx(
                      'flex h-6 w-6 items-center justify-center rounded text-sm',
                      meta.badge,
                    )}
                    aria-hidden
                  >
                    {meta.glyph}
                  </span>
                  <span className="text-sm text-navy-700">{meta.label}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-4 px-1 text-[11px] leading-snug text-ink-muted">
            Add a step, then drag from a node's bottom dot to another's top dot to make it wait on
            that step.
          </p>
        </aside>

        {/* Canvas — a real height on mobile (where it is stacked) so the graph
            is visible; fills the row from lg up. */}
        <div className="min-h-[26rem] min-w-0 flex-1 lg:min-h-0">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => setSelectedId(node.id)}
            onPaneClick={() => setSelectedId(null)}
            defaultEdgeOptions={edgeOptions}
            fitView
            proOptions={{ hideAttribution: true }}
            className="bg-surface-muted"
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={18}
              size={1}
              color="var(--integra-border-strong)"
            />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable className="!bg-surface" />
          </ReactFlow>
        </div>

        {/* Inspector */}
        <aside className="w-full shrink-0 border-t border-edge bg-surface px-4 py-4 lg:w-72 lg:border-t-0 lg:border-l">
          {selected === null ? (
            <div className="text-sm text-ink-muted">
              <p className="mb-2 font-heading text-navy-700">Nothing selected</p>
              <p className="leading-snug">
                Click a node to edit its id, action, inputs and failure mode. Select an edge and
                press Delete to remove a dependency.
              </p>
            </div>
          ) : (
            <Stack gap="md">
              <p className="font-heading text-navy-700">Edit step</p>
              <Field label="Node id" htmlFor="insp-id">
                <Input
                  id="insp-id"
                  value={selected.data.nodeId}
                  onChange={(e) => patchSelected({ nodeId: e.target.value })}
                />
              </Field>
              <Field label="Action" htmlFor="insp-action">
                <Select
                  id="insp-action"
                  value={selected.data.action}
                  onChange={(e) => patchSelected({ action: e.target.value as ActionKind })}
                >
                  {ACTION_ORDER.map((a) => (
                    <option key={a} value={a}>
                      {ACTION_META[a].label}
                    </option>
                  ))}
                </Select>
              </Field>
              <p className="-mt-1 text-[11px] leading-snug text-ink-muted">
                {ACTION_META[selected.data.action].hint}
              </p>
              <Field
                label="Failure mode"
                htmlFor="insp-fail"
                hint="halt stops the campaign; skip skips dependents; continue runs them with partial inputs."
              >
                <Select
                  id="insp-fail"
                  value={selected.data.failureMode}
                  onChange={(e) => patchSelected({ failureMode: e.target.value })}
                >
                  {FAILURE_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Inputs (JSON)"
                htmlFor="insp-inputs"
                {...(inputsValid
                  ? { hint: 'A JSON object passed to the action handler.' }
                  : { error: 'Not a JSON object: it will not save until it parses.' })}
              >
                <TextArea
                  id="insp-inputs"
                  rows={6}
                  value={selected.data.inputsText}
                  onChange={(e) => patchSelected({ inputsText: e.target.value })}
                  className="font-mono text-xs"
                />
              </Field>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setNodes((nds) => nds.filter((n) => n.id !== selected.id));
                  setEdges((eds) =>
                    eds.filter((e) => e.source !== selected.id && e.target !== selected.id),
                  );
                  setSelectedId(null);
                }}
              >
                Remove step
              </Button>
            </Stack>
          )}
        </aside>
      </div>
    </div>
  );
}

export function CampaignBuilderPage() {
  return (
    <ReactFlowProvider>
      <BuilderInner />
    </ReactFlowProvider>
  );
}
